import React, { useState, useEffect, useRef } from 'react';
import {
  IndianRupee, TrendingUp, Clock, Smartphone, CreditCard, Banknote,
  Search, ChevronLeft, ChevronRight, RefreshCw, CheckCircle, XCircle,
  QrCode, X,
} from 'lucide-react';
import {
  format, startOfDay, endOfDay, startOfWeek, endOfWeek,
  startOfMonth, endOfMonth, subDays
} from 'date-fns';
import { formatCurrency } from '../utils/numberFormat';

const API_BASE = 'https://cij3b8xauj.execute-api.ap-south-1.amazonaws.com/Prod/api';

type DatePreset = 'today' | 'yesterday' | 'week' | 'month' | 'custom';

interface CashfreeTransaction {
  eventId: string;
  type: string;
  merchantOrderId: string;   // "MINTO-618"
  cfOrderId: string;
  cfPaymentId: string;
  orderAmount: number;
  paymentAmount: number;
  paymentCurrency: string;
  paymentTime: string;
  paymentStatus: string;     // SUCCESS, FAILED, PENDING
  paymentType: string;       // UPI, CARD, NETBANKING
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  serviceCharge: number;
  serviceTax: number;
  settlementAmount: number;
}

interface TransactionResponse {
  cursor: string | null;
  data: CashfreeTransaction[];
}

const PAYMENT_TYPE_COLORS: Record<string, string> = {
  UPI:        'bg-blue-100 text-blue-800',
  CARD:       'bg-purple-100 text-purple-800',
  NETBANKING: 'bg-orange-100 text-orange-800',
  WALLET:     'bg-yellow-100 text-yellow-800',
  COD:        'bg-gray-100 text-gray-800',
};

const STATUS_COLORS: Record<string, string> = {
  SUCCESS: 'bg-green-100 text-green-800',
  FAILED:  'bg-red-100 text-red-800',
  PENDING: 'bg-yellow-100 text-yellow-800',
  FLAGGED: 'bg-orange-100 text-orange-800',
};

const getPresetDates = (preset: DatePreset): { start: string; end: string } => {
  const now = new Date();
  const fmt = (d: Date) => format(d, 'yyyy-MM-dd');
  switch (preset) {
    case 'today':     return { start: fmt(startOfDay(now)),                           end: fmt(endOfDay(now)) };
    case 'yesterday': return { start: fmt(startOfDay(subDays(now, 1))),               end: fmt(endOfDay(subDays(now, 1))) };
    case 'week':      return { start: fmt(startOfWeek(now, { weekStartsOn: 1 })),     end: fmt(endOfWeek(now, { weekStartsOn: 1 })) };
    case 'month':     return { start: fmt(startOfMonth(now)),                         end: fmt(endOfMonth(now)) };
    default:          return { start: fmt(startOfMonth(now)),                         end: fmt(endOfMonth(now)) };
  }
};

interface CollectQr { qrCodeBase64: string; amount: number; orderNumber: string; }

const PaymentsPage: React.FC = () => {
  const [transactions, setTransactions] = useState<CashfreeTransaction[]>([]);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState<string | null>(null);
  const [cursor, setCursor]             = useState<string | null>(null);
  const [cursorStack, setCursorStack]   = useState<(string | null)[]>([null]);
  const [pageIndex, setPageIndex]       = useState(0);

  // Collect Payment QR state
  const [collectAmount, setCollectAmount] = useState('');
  const [collectLabel, setCollectLabel]   = useState('');
  const [collectLoading, setCollectLoading] = useState(false);
  const [collectError, setCollectError]   = useState<string | null>(null);
  const [collectQr, setCollectQr]         = useState<CollectQr | null>(null);
  const collectInputRef = useRef<HTMLInputElement>(null);

  const handleGenerateCollectQr = async () => {
    const amt = parseFloat(collectAmount);
    if (!collectAmount || isNaN(amt) || amt <= 0) {
      setCollectError('Enter a valid amount greater than 0.');
      return;
    }
    setCollectError(null);
    setCollectQr(null);
    setCollectLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ amount: amt.toString() });
      if (collectLabel.trim()) params.set('label', collectLabel.trim());
      const res = await fetch(`${API_BASE}/v1/payment/collect-qr?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || `Error ${res.status}`);
      }
      const data: CollectQr = await res.json();
      setCollectQr(data);
    } catch (e: any) {
      setCollectError(e.message ?? 'Failed to generate QR');
    } finally {
      setCollectLoading(false);
    }
  };

  const [preset, setPreset]       = useState<DatePreset>('month');
  const [startDate, setStartDate] = useState(getPresetDates('month').start);
  const [endDate, setEndDate]     = useState(getPresetDates('month').end);
  const [search, setSearch]       = useState('');

  const LIMIT = 50;

  useEffect(() => {
    fetchPage(null, 0);
  }, [startDate, endDate]);

  const fetchPage = async (cur: string | null, idx: number) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        startDate,
        endDate,
        limit: LIMIT.toString(),
        ...(cur ? { cursor: cur } : {}),
      });
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/v1/payment/transactions?${params}`, {
        headers: {
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (res.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('isLoggedIn');
        window.location.href = '/login';
        return;
      }
      if (!res.ok) {
        const message = await res.text();
        throw new Error(message || `API error ${res.status}`);
      }
      const data: TransactionResponse = await res.json();
      setTransactions(data.data ?? []);
      setCursor(data.cursor ?? null);
      setPageIndex(idx);
    } catch (e: any) {
      setError(e.message ?? 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const applyPreset = (p: DatePreset) => {
    setPreset(p);
    if (p !== 'custom') {
      const { start, end } = getPresetDates(p);
      setStartDate(start);
      setEndDate(end);
    }
    setCursorStack([null]);
    setPageIndex(0);
  };

  const handleNextPage = () => {
    if (!cursor) return;
    const newStack = [...cursorStack, cursor];
    setCursorStack(newStack);
    fetchPage(cursor, pageIndex + 1);
  };

  const handlePrevPage = () => {
    if (pageIndex === 0) return;
    const newStack = [...cursorStack];
    newStack.pop();
    setCursorStack(newStack);
    fetchPage(newStack[newStack.length - 1], pageIndex - 1);
  };

  // Client-side search filter
  const filtered = search.trim()
    ? transactions.filter(t =>
        t.merchantOrderId?.toLowerCase().includes(search.toLowerCase()) ||
        t.customerName?.toLowerCase().includes(search.toLowerCase()) ||
        t.customerPhone?.includes(search) ||
        t.cfPaymentId?.toLowerCase().includes(search.toLowerCase())
      )
    : transactions;

  // Summaries from current page
  const successTx  = filtered.filter(t => t.paymentStatus === 'SUCCESS');
  const failedTx   = filtered.filter(t => t.paymentStatus === 'FAILED');
  const totalCollected  = successTx.reduce((s, t) => s + t.paymentAmount, 0);
  const totalSettlement = successTx.reduce((s, t) => s + t.settlementAmount, 0);
  const totalFees       = successTx.reduce((s, t) => s + t.serviceCharge + t.serviceTax, 0);

  const upiTotal  = successTx.filter(t => t.paymentType === 'UPI').reduce((s, t) => s + t.paymentAmount, 0);
  const cardTotal = successTx.filter(t => t.paymentType === 'CARD').reduce((s, t) => s + t.paymentAmount, 0);
  const nbTotal   = successTx.filter(t => t.paymentType === 'NETBANKING').reduce((s, t) => s + t.paymentAmount, 0);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payments</h1>
          <p className="text-sm text-gray-500 mt-0.5">Live data from Cashfree</p>
        </div>
        <button
          onClick={() => fetchPage(cursorStack[pageIndex], pageIndex)}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {/* Collect Payment QR */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="p-2 rounded-lg bg-indigo-50">
            <QrCode className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-gray-900">Collect Payment</h2>
            <p className="text-xs text-gray-400">Generate a UPI QR code to receive any amount into Minto's Cashfree account</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Amount (₹)</label>
            <input
              ref={collectInputRef}
              type="number"
              min="1"
              step="1"
              value={collectAmount}
              onChange={e => setCollectAmount(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleGenerateCollectQr()}
              placeholder="e.g. 500"
              className="w-36 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-300"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Label (optional)</label>
            <input
              type="text"
              value={collectLabel}
              onChange={e => setCollectLabel(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleGenerateCollectQr()}
              placeholder="e.g. Advance payment"
              className="w-52 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-300"
            />
          </div>
          <button
            onClick={handleGenerateCollectQr}
            disabled={collectLoading}
            className="flex items-center gap-2 px-5 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {collectLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <QrCode className="h-4 w-4" />}
            {collectLoading ? 'Generating...' : 'Generate QR'}
          </button>
        </div>

        {collectError && (
          <p className="mt-3 text-sm text-red-600">{collectError}</p>
        )}

        {collectQr && (
          <div className="mt-5 flex flex-col items-center gap-3 p-5 bg-gray-50 rounded-xl border border-gray-100 w-fit">
            <div className="flex items-center justify-between w-full">
              <div>
                <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Amount to collect</p>
                <p className="text-2xl font-black text-gray-900">₹{collectQr.amount.toLocaleString('en-IN')}</p>
                {collectQr.orderNumber && collectQr.orderNumber !== 'Minto Collection' && (
                  <p className="text-xs text-gray-500 mt-0.5">{collectQr.orderNumber}</p>
                )}
              </div>
              <button
                onClick={() => { setCollectQr(null); setCollectAmount(''); setCollectLabel(''); collectInputRef.current?.focus(); }}
                className="p-1.5 rounded-full hover:bg-gray-200 text-gray-400"
                title="Clear"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <img
              src={collectQr.qrCodeBase64}
              alt="UPI QR code"
              className="w-56 h-56 rounded-lg border border-gray-200 bg-white p-2 object-contain"
            />
            <p className="text-xs text-gray-400 text-center">
              Scan with any UPI app · PhonePe · GPay · Paytm
            </p>
          </div>
        )}
      </div>

      {/* Date Presets */}
      <div className="flex flex-wrap items-center gap-2">
        {(['today', 'yesterday', 'week', 'month'] as DatePreset[]).map(p => (
          <button
            key={p}
            onClick={() => applyPreset(p)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              preset === p
                ? 'bg-indigo-600 text-white'
                : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {p.charAt(0).toUpperCase() + p.slice(1)}
          </button>
        ))}
        <div className="flex items-center gap-2 ml-2">
          <input type="date" value={startDate}
            onChange={e => { setPreset('custom'); setStartDate(e.target.value); }}
            className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700"
          />
          <span className="text-gray-400 text-sm">to</span>
          <input type="date" value={endDate}
            onChange={e => { setPreset('custom'); setEndDate(e.target.value); }}
            className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700"
          />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard
          icon={<IndianRupee className="h-5 w-5 text-green-600" />}
          iconBg="bg-green-50"
          label="Total Collected"
          value={formatCurrency(totalCollected)}
          sub={`${successTx.length} successful`}
        />
        <SummaryCard
          icon={<TrendingUp className="h-5 w-5 text-indigo-600" />}
          iconBg="bg-indigo-50"
          label="Net Settlement"
          value={formatCurrency(totalSettlement)}
          sub={`After fees: ${formatCurrency(totalFees)}`}
        />
        <SummaryCard
          icon={<CheckCircle className="h-5 w-5 text-blue-600" />}
          iconBg="bg-blue-50"
          label="UPI"
          value={formatCurrency(upiTotal)}
          sub={`Card: ${formatCurrency(cardTotal)}`}
        />
        <SummaryCard
          icon={<XCircle className="h-5 w-5 text-red-500" />}
          iconBg="bg-red-50"
          label="Failed"
          value={failedTx.length.toString()}
          sub="transactions"
        />
      </div>

      {/* Search */}
      <div className="flex items-center gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search order / customer / payment ID..."
            className="pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 w-72"
          />
        </div>
        <span className="text-sm text-gray-400 ml-auto">{filtered.length} transactions on this page</span>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Settlement</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Fees</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date & Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={9} className="text-center py-12 text-gray-400 text-sm">Loading from Cashfree...</td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-12 text-gray-400 text-sm">No transactions found</td>
                </tr>
              ) : (
                filtered.map((tx, i) => (
                  <tr key={tx.cfPaymentId || i} className={`hover:bg-gray-50 transition-colors ${tx.paymentStatus === 'FAILED' ? 'opacity-60' : ''}`}>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm font-medium text-indigo-600">{tx.merchantOrderId || '-'}</div>
                      <div className="text-xs text-gray-400">{tx.cfOrderId}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-xs text-gray-500 font-mono">{tx.cfPaymentId || '-'}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{tx.customerName || '-'}</div>
                      <div className="text-xs text-gray-400">{tx.customerPhone}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900 whitespace-nowrap">
                      {formatCurrency(tx.paymentAmount)}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-medium text-green-700 whitespace-nowrap">
                      {formatCurrency(tx.settlementAmount)}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-red-500 whitespace-nowrap">
                      {formatCurrency(tx.serviceCharge + tx.serviceTax)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        PAYMENT_TYPE_COLORS[tx.paymentType] ?? 'bg-gray-100 text-gray-800'
                      }`}>
                        {tx.paymentType || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        STATUS_COLORS[tx.paymentStatus] ?? 'bg-gray-100 text-gray-800'
                      }`}>
                        {tx.paymentStatus || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                      {tx.paymentTime
                        ? new Date(tx.paymentTime).toLocaleString('en-IN', {
                            day: 'numeric', month: 'short', year: 'numeric',
                            hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata'
                          })
                        : '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
          <span className="text-sm text-gray-500">Page {pageIndex + 1}</span>
          <div className="flex gap-2">
            <button
              onClick={handlePrevPage}
              disabled={pageIndex === 0 || loading}
              className="p-1.5 rounded border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={handleNextPage}
              disabled={!cursor || loading}
              className="p-1.5 rounded border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Footer totals */}
      {filtered.length > 0 && (
        <div className="bg-indigo-50 rounded-xl border border-indigo-100 p-4 flex flex-wrap gap-6">
          <Stat label="Transactions" value={filtered.length.toString()} />
          <Stat label="Collected" value={formatCurrency(totalCollected)} highlight />
          <Stat label="Net Settlement" value={formatCurrency(totalSettlement)} />
          <Stat label="Cashfree Fees" value={formatCurrency(totalFees)} warn />
          <Stat label="UPI" value={formatCurrency(upiTotal)} />
          <Stat label="Card" value={formatCurrency(cardTotal)} />
          <Stat label="Net Banking" value={formatCurrency(nbTotal)} />
        </div>
      )}
    </div>
  );
};

interface SummaryCardProps {
  icon: React.ReactNode; iconBg: string; label: string; value: string; sub?: string;
}
const SummaryCard: React.FC<SummaryCardProps> = ({ icon, iconBg, label, value, sub }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4">
    <div className="flex items-center gap-3 mb-3">
      <div className={`p-2 rounded-lg ${iconBg}`}>{icon}</div>
      <span className="text-sm text-gray-500 font-medium">{label}</span>
    </div>
    <div className="text-2xl font-bold text-gray-900">{value}</div>
    {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
  </div>
);

interface StatProps { label: string; value: string; highlight?: boolean; warn?: boolean; }
const Stat: React.FC<StatProps> = ({ label, value, highlight, warn }) => (
  <div>
    <div className="text-xs text-gray-500 mb-0.5">{label}</div>
    <div className={`text-lg font-bold ${highlight ? 'text-green-700' : warn ? 'text-red-600' : 'text-gray-800'}`}>
      {value}
    </div>
  </div>
);

export default PaymentsPage;
